import { isMonetaryValueDataObject, MonetaryValueDataObjectType, MoneyPerAreaValue } from 'ComponentLibrary/src/common/money'
import { forwardRef } from 'react'
import { Control, FieldPathByValue, FieldPathValue, FieldValues, UseControllerProps, useController } from 'react-hook-form'
import { MonetaryInput, MonetaryInputProps } from '../MonetaryInput/MonetaryInput'
import { isRequiredRule } from '../TextInput/TextInput'

export type MoneyPerAreaInputProps = Omit<MonetaryInputProps, 'onValueChange'> & {
    onValueChange?: (value: MoneyPerAreaValue | undefined) => void
}

export const MoneyPerAreaInput = forwardRef<HTMLInputElement, MoneyPerAreaInputProps>(function MoneyPerAreaInput(props, ref) {
    const { onValueChange, unit = '€/m²', ...monetaryInputProps } = props

    return (
        <MonetaryInput
            {...monetaryInputProps}
            onValueChange={(nextValue) => {
                onValueChange?.(nextValue?.toMoneyPerAreaValue())
            }}
            ref={ref}
            unit={unit}
        />
    )
})

type MoneyPerAreaFieldPath<TFieldValues extends FieldValues> = FieldPathByValue<TFieldValues, MonetaryValueDataObjectType | undefined>

type FormMoneyPerAreaInputRules<TFieldValues extends FieldValues, TName extends MoneyPerAreaFieldPath<TFieldValues>> = Omit<
    NonNullable<UseControllerProps<TFieldValues, TName>['rules']>,
    'max' | 'maxLength' | 'min' | 'minLength' | 'pattern' | 'valueAsNumber'
>

export type FormMoneyPerAreaInputProps<
    TFieldValues extends FieldValues,
    TName extends MoneyPerAreaFieldPath<TFieldValues> = MoneyPerAreaFieldPath<TFieldValues>,
> = Omit<MoneyPerAreaInputProps, 'defaultValue' | 'errorMessage' | 'name' | 'onValueChange' | 'required' | 'value'> & {
    control: Control<TFieldValues>
    defaultValue?: UseControllerProps<TFieldValues, TName>['defaultValue']
    name: TName
    rules?: FormMoneyPerAreaInputRules<TFieldValues, TName>
}

export function FormMoneyPerAreaInput<
    TFieldValues extends FieldValues,
    TName extends MoneyPerAreaFieldPath<TFieldValues> = MoneyPerAreaFieldPath<TFieldValues>,
>({ control, defaultValue, name, onBlur, rules, ...moneyPerAreaInputProps }: FormMoneyPerAreaInputProps<TFieldValues, TName>) {
    const { validate, ...otherRules } = rules ?? {}
    const { field, fieldState } = useController({
        control,
        defaultValue,
        name,
        rules: {
            ...otherRules,
            validate:
                validate ??
                ((fieldValue: FieldPathValue<TFieldValues, TName>) =>
                    fieldValue === undefined || isMonetaryValueDataObject(fieldValue) || 'Bitte geben Sie eine Zahl ein'),
        },
    })

    return (
        <MoneyPerAreaInput
            {...moneyPerAreaInputProps}
            errorMessage={fieldState.error ? (fieldState.error.message?.toString() ?? '') : undefined}
            name={field.name}
            onBlur={(event) => {
                field.onBlur()
                onBlur?.(event)
            }}
            onValueChange={field.onChange}
            ref={field.ref}
            required={isRequiredRule(rules?.required)}
            value={field.value}
        />
    )
}
